import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { formatCurrency, formatDate, humanizeStatus } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ── Constants ──────────────────────────────────────────────────────────────

const LOAN_TYPES = [
  { value: 'personal',  label: 'Personal Loan' },
  { value: 'business',  label: 'Business Loan' },
  { value: 'mortgage',  label: 'Mortgage' },
  { value: 'auto',      label: 'Auto Loan' },
  { value: 'education', label: 'Education Loan' },
  { value: 'emergency', label: 'Emergency Loan' },
];

const TIERS = ['basic', 'standard', 'gold', 'premium'];

const TIER_COLORS = {
  basic:    { bg: 'rgba(148,163,184,0.12)', color: '#94a3b8' },
  standard: { bg: 'rgba(91,142,255,0.12)',  color: '#5B8EFF' },
  gold:     { bg: 'rgba(245,166,35,0.12)',  color: '#F5A623' },
  premium:  { bg: 'rgba(129,140,248,0.12)', color: '#818CF8' },
};

const TYPE_COLORS = {
  personal:  '#00C896', business: '#5B8EFF', mortgage: '#818CF8',
  auto:      '#F5A623', education: '#ff9900', emergency: '#FF4757',
};

const DOC_REQUIREMENTS = [
  { key: 'requires_bank_statement',       label: 'Bank Statement' },
  { key: 'requires_id_card',              label: 'ID Card' },
  { key: 'requires_proof_of_address',     label: 'Proof of Address' },
  { key: 'requires_pay_slip',             label: 'Pay Slip' },
  { key: 'requires_tax_return',           label: 'Tax Return' },
  { key: 'requires_business_registration', label: 'Business Registration' },
];

const EMPTY_FORM = {
  name: '', description: '', loan_type: 'personal',
  min_amount: '', max_amount: '',
  min_tenure_months: '', max_tenure_months: '',
  base_interest_rate: '', processing_fee_rate: '2.00',
  is_active: true,
  eligible_tiers: [],
  tier_discounts: { basic: '', standard: '', gold: '', premium: '' },
  requires_bank_statement: false, requires_id_card: false,
  requires_proof_of_address: false, requires_pay_slip: false,
  requires_tax_return: false, requires_business_registration: false,
};

// ── Small UI atoms ─────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const color = TYPE_COLORS[type] || '#94a3b8';
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.55rem',
      borderRadius: 99, textTransform: 'capitalize',
      background: `${color}18`, color, border: `1px solid ${color}28`,
    }}>
      {type}
    </span>
  );
}

function TierBadge({ tier }) {
  const s = TIER_COLORS[tier] || TIER_COLORS.basic;
  return (
    <span style={{
      fontSize: '0.6rem', fontWeight: 700, padding: '0.15rem 0.45rem',
      borderRadius: 99, textTransform: 'uppercase',
      background: s.bg, color: s.color,
    }}>
      {tier}
    </span>
  );
}

function StatusPill({ active }) {
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.5rem',
      borderRadius: 99, textTransform: 'uppercase',
      background: active ? 'rgba(0,200,150,0.12)' : 'rgba(255,71,87,0.12)',
      color: active ? '#00C896' : '#FF4757',
    }}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function FormRow({ label, children, required }) {
  return (
    <div className="form-group" style={{ marginBottom: '0.9rem' }}>
      <label className="form-label" style={{ fontSize: '0.78rem' }}>
        {label}{required && <span style={{ color: '#FF4757', marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ── Product form modal ─────────────────────────────────────────────────────

function ProductModal({ product, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!product;

  const buildInitialForm = () => {
    if (!product) return EMPTY_FORM;
    const discounts = { basic: '', standard: '', gold: '', premium: '' };
    (product.tier_discounts || []).forEach(d => { discounts[d.tier] = d.discount_rate; });
    return {
      name: product.name || '',
      description: product.description || '',
      loan_type: product.loan_type || 'personal',
      min_amount: product.min_amount || '',
      max_amount: product.max_amount || '',
      min_tenure_months: product.min_tenure_months || '',
      max_tenure_months: product.max_tenure_months || '',
      base_interest_rate: product.base_interest_rate || '',
      processing_fee_rate: product.processing_fee_rate
        ? (parseFloat(product.processing_fee_rate) * 100).toFixed(2)
        : '2.00',
      is_active: product.is_active ?? true,
      eligible_tiers: product.eligible_tiers || [],
      tier_discounts: discounts,
      requires_bank_statement: product.requires_bank_statement ?? false,
      requires_id_card: product.requires_id_card ?? false,
      requires_proof_of_address: product.requires_proof_of_address ?? false,
      requires_pay_slip: product.requires_pay_slip ?? false,
      requires_tax_return: product.requires_tax_return ?? false,
      requires_business_registration: product.requires_business_registration ?? false,
    };
  };

  const [form, setForm] = useState(buildInitialForm);
  const [err, setErr] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setDiscount = (tier, val) => setForm(f => ({
    ...f, tier_discounts: { ...f.tier_discounts, [tier]: val },
  }));
  const toggleEligibleTier = (tier) => setForm(f => {
    const tiers = f.eligible_tiers.includes(tier)
      ? f.eligible_tiers.filter(t => t !== tier)
      : [...f.eligible_tiers, tier];
    return { ...f, eligible_tiers: tiers };
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => isEdit
      ? adminService.updateLoanProduct(product.id, payload)
      : adminService.createLoanProduct(payload),
    onSuccess: async (res) => {
      const productId = res.data.data?.id || product?.id;
      const discountPayload = TIERS
        .filter(t => form.tier_discounts[t] !== '' && form.tier_discounts[t] !== null)
        .map(t => ({ tier: t, discount_rate: parseFloat(form.tier_discounts[t]) }));
      if (discountPayload.length > 0 && productId) {
        await adminService.setTierDiscounts(productId, discountPayload).catch(() => {});
      }
      qc.invalidateQueries({ queryKey: ['admin-loan-products'] });
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Failed to save product.'),
  });

  const handleSubmit = () => {
    if (!form.name.trim()) { setErr('Product name is required.'); return; }
    if (!form.loan_type) { setErr('Loan type is required.'); return; }
    if (!form.min_amount || !form.max_amount) { setErr('Amount range is required.'); return; }
    if (parseFloat(form.min_amount) >= parseFloat(form.max_amount)) { setErr('Max amount must be greater than min amount.'); return; }
    if (!form.min_tenure_months || !form.max_tenure_months) { setErr('Tenure range is required.'); return; }
    if (parseInt(form.min_tenure_months) >= parseInt(form.max_tenure_months)) { setErr('Max tenure must be greater than min tenure.'); return; }
    if (!form.base_interest_rate) { setErr('Base interest rate is required.'); return; }
    setErr('');
    saveMutation.mutate({
      name: form.name.trim(),
      description: form.description.trim(),
      loan_type: form.loan_type,
      min_amount: parseFloat(form.min_amount),
      max_amount: parseFloat(form.max_amount),
      min_tenure_months: parseInt(form.min_tenure_months),
      max_tenure_months: parseInt(form.max_tenure_months),
      base_interest_rate: parseFloat(form.base_interest_rate),
      processing_fee_rate: parseFloat(form.processing_fee_rate || 2) / 100,
      eligible_tiers: form.eligible_tiers,
      is_active: form.is_active,
      requires_bank_statement: form.requires_bank_statement,
      requires_id_card: form.requires_id_card,
      requires_proof_of_address: form.requires_proof_of_address,
      requires_pay_slip: form.requires_pay_slip,
      requires_tax_return: form.requires_tax_return,
      requires_business_registration: form.requires_business_registration,
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div className="card" style={{ width: '100%', maxWidth: 900, maxHeight: '92vh', overflow: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{isEdit ? 'Edit Loan Product' : 'New Loan Product'}</h2>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-2)', margin: '0.2rem 0 0' }}>
              {isEdit ? `Editing: ${product.name}` : 'Configure a new product users can apply for'}
            </p>
          </div>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>

        {/* Core fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <FormRow label="Product Name" required>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Quick Personal Loan" />
            </FormRow>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <FormRow label="Description">
              <textarea className="form-input" value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="What is this product for? What are the key benefits?" rows={2}
                style={{ resize: 'vertical', minHeight: 60 }} />
            </FormRow>
          </div>

          <FormRow label="Loan Type" required>
            <select className="form-input" value={form.loan_type} onChange={e => set('loan_type', e.target.value)}>
              {LOAN_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
            </select>
          </FormRow>

          <FormRow label="Status">
            <select className="form-input" value={form.is_active ? 'true' : 'false'} onChange={e => set('is_active', e.target.value === 'true')}>
              <option value="true">Active — visible to users</option>
              <option value="false">Inactive — hidden from users</option>
            </select>
          </FormRow>
        </div>

        {/* Amount range */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.9rem', marginBottom: '0.9rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', marginBottom: '0.75rem' }}>Loan Amount Range</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <FormRow label="Minimum Amount (₦)" required>
              <input className="form-input" type="number" min="0" value={form.min_amount} onChange={e => set('min_amount', e.target.value)} placeholder="e.g. 50000" />
            </FormRow>
            <FormRow label="Maximum Amount (₦)" required>
              <input className="form-input" type="number" min="0" value={form.max_amount} onChange={e => set('max_amount', e.target.value)} placeholder="e.g. 5000000" />
            </FormRow>
          </div>
        </div>

        {/* Tenure range */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.9rem', marginBottom: '0.9rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', marginBottom: '0.75rem' }}>Tenure Range</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <FormRow label="Minimum Tenure (months)" required>
              <input className="form-input" type="number" min="1" value={form.min_tenure_months} onChange={e => set('min_tenure_months', e.target.value)} placeholder="e.g. 3" />
            </FormRow>
            <FormRow label="Maximum Tenure (months)" required>
              <input className="form-input" type="number" min="1" value={form.max_tenure_months} onChange={e => set('max_tenure_months', e.target.value)} placeholder="e.g. 36" />
            </FormRow>
          </div>
        </div>

        {/* Rates */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.9rem', marginBottom: '0.9rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', marginBottom: '0.75rem' }}>Interest & Fees</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <FormRow label="Base Interest Rate (% p.a.)" required>
              <input className="form-input" type="number" step="0.01" min="0" max="100" value={form.base_interest_rate} onChange={e => set('base_interest_rate', e.target.value)} placeholder="e.g. 24.00" />
            </FormRow>
            <FormRow label="Processing Fee (%)">
              <input className="form-input" type="number" step="0.01" min="0" max="100" value={form.processing_fee_rate} onChange={e => set('processing_fee_rate', e.target.value)} placeholder="e.g. 2.00" />
            </FormRow>
          </div>
        </div>

        {/* Eligible tiers */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.9rem', marginBottom: '0.9rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 4 }}>Eligible Tiers</div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-2)', margin: '0 0 0.75rem' }}>
            Select which account tiers can see and apply for this product. Leave all unchecked to make it available to all tiers.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {TIERS.map(tier => {
              const selected = form.eligible_tiers.includes(tier);
              const s = TIER_COLORS[tier];
              return (
                <label key={tier} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer',
                  padding: '0.45rem 0.85rem', borderRadius: 8,
                  background: selected ? s.bg : 'var(--surface-alt)',
                  border: `1px solid ${selected ? s.color + '60' : 'var(--border)'}`,
                  transition: 'all .15s',
                }}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleEligibleTier(tier)}
                    style={{ accentColor: s.color, width: 14, height: 14 }}
                  />
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: selected ? s.color : 'var(--text-2)', textTransform: 'capitalize' }}>
                    {tier}
                  </span>
                </label>
              );
            })}
          </div>
          {form.eligible_tiers.length === 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: 'var(--text-3)' }}>
              ✓ All tiers can access this product
            </div>
          )}
        </div>

        {/* Required Documents */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.9rem', marginBottom: '0.9rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 4 }}>Required Documents</div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-2)', margin: '0 0 0.75rem' }}>
            Documents the maker may request from the applicant during review. Defaults to off — the maker can still request any of these ad hoc.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem' }}>
            {DOC_REQUIREMENTS.map(({ key, label }) => {
              const checked = !!form[key];
              return (
                <label key={key} style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.5rem 0.75rem', borderRadius: 8, cursor: 'pointer',
                  background: checked ? 'rgba(91,142,255,0.08)' : 'var(--surface-alt)',
                  border: `1px solid ${checked ? 'rgba(91,142,255,0.35)' : 'var(--border)'}`,
                  transition: 'all .15s',
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => set(key, !checked)}
                    style={{ accentColor: '#5B8EFF', width: 14, height: 14, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '0.78rem', fontWeight: checked ? 600 : 400, color: checked ? '#5B8EFF' : 'var(--text-2)' }}>
                    {label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Tier discounts */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.9rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 4 }}>Tier Discounts</div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-2)', margin: '0 0 0.75rem' }}>
            Rate reduction in percentage points applied on top of the base rate for each tier. Leave blank for no discount.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 0.75rem' }}>
            {TIERS.map(tier => (
              <div key={tier}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: 6 }}>
                  <TierBadge tier={tier} />
                </div>
                <input
                  className="form-input"
                  type="number" step="0.01" min="0"
                  value={form.tier_discounts[tier]}
                  onChange={e => setDiscount(tier, e.target.value)}
                  placeholder="0.00"
                  style={{ fontSize: '0.82rem' }}
                />
                {form.base_interest_rate && form.tier_discounts[tier] ? (
                  <div style={{ fontSize: '0.68rem', color: 'var(--primary)', marginTop: 3 }}>
                    → {Math.max(0, parseFloat(form.base_interest_rate) - parseFloat(form.tier_discounts[tier])).toFixed(2)}% effective
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={saveMutation.isPending} onClick={handleSubmit}>
            {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Product'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Product detail panel ───────────────────────────────────────────────────

function ProductDetail({ product, onEdit, onDeactivate, onClose }) {
  const effectiveRates = TIERS.map(tier => {
    const discount = (product.tier_discounts || []).find(d => d.tier === tier);
    const base = parseFloat(product.base_interest_rate);
    const effective = discount ? Math.max(0, base - parseFloat(discount.discount_rate)) : base;
    return { tier, discount: discount?.discount_rate || null, effective };
  });

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', background: 'var(--surface)', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Detail header */}
      <div style={{ padding: '1rem 1.25rem', background: `${TYPE_COLORS[product.loan_type] || '#94a3b8'}10`, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
            <TypeBadge type={product.loan_type} />
            <StatusPill active={product.is_active} />
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{product.name}</h3>
          {product.description && <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', margin: '0.25rem 0 0', lineHeight: 1.5 }}>{product.description}</p>}
        </div>
        <button className="btn btn-ghost" style={{ padding: '0.2rem 0.4rem', flexShrink: 0 }} onClick={onClose}>✕</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '1rem 1.25rem' }}>
        {/* Key metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.6rem', marginBottom: '1.25rem' }}>
          {[
            { label: 'Amount Range', value: `${formatCurrency(product.min_amount)} – ${formatCurrency(product.max_amount)}` },
            { label: 'Tenure', value: `${product.min_tenure_months} – ${product.max_tenure_months} months` },
            { label: 'Base Interest Rate', value: `${product.base_interest_rate}% p.a.` },
            { label: 'Processing Fee', value: `${(parseFloat(product.processing_fee_rate) * 100).toFixed(2)}%` },
            { label: 'Created', value: formatDate(product.created_at) },
            { label: 'Last Updated', value: formatDate(product.updated_at) },
          ].map(({ label, value }) => (
            <div key={label} style={{ padding: '0.6rem 0.75rem', background: 'var(--surface-alt)', borderRadius: 8 }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Eligible tiers */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', marginBottom: '0.6rem' }}>Eligible Tiers</div>
          {product.eligible_tiers?.length > 0 ? (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {product.eligible_tiers.map(tier => <TierBadge key={tier} tier={tier} />)}
            </div>
          ) : (
            <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>All tiers — no restriction</span>
          )}
        </div>

        {/* Tier rate table */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)', marginBottom: '0.6rem' }}>Effective Rates by Tier</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {effectiveRates.map(({ tier, discount, effective }) => (
              <div key={tier} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'var(--surface-alt)', borderRadius: 8, gap: '0.75rem' }}>
                <TierBadge tier={tier} />
                <div style={{ flex: 1, fontSize: '0.82rem', color: 'var(--text-2)' }}>
                  {discount ? (
                    <span>{product.base_interest_rate}% <span style={{ color: '#00C896' }}>− {discount}%</span></span>
                  ) : <span style={{ color: 'var(--text-3)' }}>No discount</span>}
                </div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: TIER_COLORS[tier]?.color }}>
                  {effective.toFixed(2)}% p.a.
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ padding: '0.875rem 1.25rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.6rem' }}>
        <button className="btn btn-primary" style={{ flex: 1, fontSize: '0.82rem' }} onClick={onEdit}>Edit Product</button>
        <button
          className="btn btn-ghost"
          style={{ fontSize: '0.82rem', color: product.is_active ? '#FF4757' : '#1596E0', borderColor: product.is_active ? 'rgba(255,71,87,0.3)' : 'rgba(21,150,224,0.3)' }}
          onClick={onDeactivate}
        >
          {product.is_active ? 'Deactivate' : 'Activate'}
        </button>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function LoanProducts() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [modal, setModal] = useState(null);   // null | 'new' | product object (edit)
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-loan-products', filterType, filterStatus],
    queryFn: () => {
      const params = {};
      if (filterType) params.loan_type = filterType;
      if (filterStatus !== '') params.is_active = filterStatus;
      return adminService.getLoanProducts(params).then(r => r.data.data);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }) => is_active
      ? adminService.deactivateLoanProduct(id)
      : adminService.updateLoanProduct(id, { is_active: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-loan-products'] });
      setSelected(null);
      setErr('');
    },
    onError: (e) => setErr(e.response?.data?.message || 'Action failed.'),
  });

  const products = data?.results || data || [];

  const filtered = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.loan_type.toLowerCase().includes(search.toLowerCase())
  );

  const summary = {
    total: products.length,
    active: products.filter(p => p.is_active).length,
    types: [...new Set(products.map(p => p.loan_type))].length,
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Loan Products</h1>
          <p>Manage the loan products available to customers based on their account tier</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>+ New Product</button>
      </div>

      {err && <div className="alert alert-error" style={{ marginBottom: '1rem' }}><span>⚠</span><span>{err}</span></div>}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Total Products', value: summary.total, color: 'var(--primary)' },
          { label: 'Active Products', value: summary.active, color: '#00C896' },
          { label: 'Loan Types Covered', value: summary.types, color: '#5B8EFF' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: '1rem' }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          className="form-input" style={{ maxWidth: 240, fontSize: '0.85rem' }}
          placeholder="Search by name or type…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <select className="form-input" style={{ maxWidth: 180, fontSize: '0.85rem' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {LOAN_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
        </select>
        <select className="form-input" style={{ maxWidth: 160, fontSize: '0.85rem' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
      </div>

      {/* Split layout: table + detail panel */}
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: '1rem', alignItems: 'start' }}>

        {/* Products table */}
        <div className="card" style={{ padding: 0 }}>
          {isLoading ? (
            <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Type</th>
                    <th>Amount Range</th>
                    <th>Tenure</th>
                    <th>Base Rate</th>
                    <th>Proc. Fee</th>
                    <th>Eligible Tiers</th>
                    <th>Discounts</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2.5rem' }}>
                        {search ? 'No products match your search.' : 'No loan products yet. Create one to get started.'}
                      </td>
                    </tr>
                  ) : filtered.map(p => (
                    <tr
                      key={p.id}
                      style={{ cursor: 'pointer', background: selected?.id === p.id ? 'rgba(0,200,150,0.04)' : undefined }}
                      onClick={() => setSelected(selected?.id === p.id ? null : p)}
                    >
                      <td>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.name}</div>
                        {p.description && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.description}
                          </div>
                        )}
                      </td>
                      <td><TypeBadge type={p.loan_type} /></td>
                      <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {formatCurrency(p.min_amount)}<br />
                        <span style={{ color: 'var(--text-3)' }}>to {formatCurrency(p.max_amount)}</span>
                      </td>
                      <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {p.min_tenure_months}–{p.max_tenure_months} <span style={{ color: 'var(--text-3)' }}>mo</span>
                      </td>
                      <td style={{ fontSize: '0.875rem', fontWeight: 600 }}>{p.base_interest_rate}%</td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>
                        {(parseFloat(p.processing_fee_rate) * 100).toFixed(2)}%
                      </td>
                      <td>
                        {p.eligible_tiers?.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
                            {p.eligible_tiers.map(t => <TierBadge key={t} tier={t} />)}
                          </div>
                        ) : <span style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>All tiers</span>}
                      </td>
                      <td>
                        {p.tier_discounts?.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
                            {p.tier_discounts.map(d => (
                              <span key={d.tier} style={{
                                fontSize: '0.6rem', fontWeight: 600, padding: '0.1rem 0.35rem',
                                borderRadius: 99, background: TIER_COLORS[d.tier]?.bg, color: TIER_COLORS[d.tier]?.color,
                              }}>
                                {d.tier} −{d.discount_rate}%
                              </span>
                            ))}
                          </div>
                        ) : <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>None</span>}
                      </td>
                      <td><StatusPill active={p.is_active} /></td>
                      <td>
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
                          onClick={e => { e.stopPropagation(); setModal(p); }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <ProductDetail
            product={selected}
            onEdit={() => { setModal(selected); }}
            onDeactivate={() => toggleMutation.mutate({ id: selected.id, is_active: selected.is_active })}
            onClose={() => setSelected(null)}
          />
        )}
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <ProductModal
          product={modal === 'new' ? null : modal}
          onClose={() => { setModal(null); qc.invalidateQueries({ queryKey: ['admin-loan-products'] }); }}
        />
      )}
    </div>
  );
}
